import { Editor } from '@tiptap/core'
import { useEditor } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import { Markdown } from 'tiptap-markdown'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'

let viewerEditor = null
let detachedEditor = null

/** 序列化所有 contentToHtml，避免並行轉換互相覆寫 editor 狀態 */
let contentToHtmlQueue = Promise.resolve()

const getViewerExtensions = () => [
  StarterKit,
  Markdown.configure({
    breaks: true,
  }),
  TextStyle,
  Color,
  Table.configure({
    resizable: true,
  }),
  TableRow,
  TableCell,
  TableHeader,
  Link.configure({
    HTMLAttributes: {
      rel: null,
      target: null,
    },
  }),
  Image,
]

const isTiptapDoc = v => (
  !!v && typeof v === 'object' && !Array.isArray(v) && v.type === 'doc'
)

const isTiptapFragment = v => (
  Array.isArray(v)
  && v.length > 0
  && v.every(n => n && typeof n === 'object' && typeof n.type === 'string')
)

/** 只把 Markdown / TipTap JSON 丟進 editor；其它物件一律當純文字，避免吃到畫面 DOM */
const normalizeEditorContent = (content) => {
  if (content == null || content === '') return ''
  if (typeof content === 'string') return content
  if (isTiptapDoc(content)) return content
  if (isTiptapFragment(content)) return { type: 'doc', content }
  if (typeof content === 'object') {
    try {
      return JSON.stringify(content, null, 2)
    } catch (e) {
      return String(content)
    }
  }
  return String(content)
}

const getDetachedViewerEditor = () => {
  if (!detachedEditor || detachedEditor.isDestroyed) {
    detachedEditor = new Editor({
      element: document.createElement('div'),
      content: '',
      editable: false,
      extensions: getViewerExtensions(),
    })
  }
  return detachedEditor
}

export const useViewerEditor = () => {
  if (viewerEditor?.value) viewerEditor.value.destroy()

  viewerEditor = useEditor({
    content: '',
    extensions: getViewerExtensions(),
    editable: false,
  })

  return viewerEditor
}

const convertContentToHtmlSync = (content) => {
  if (content == null || content === '') return ''
  if (typeof content === 'string' && content.startsWith('<p>')) return content

  const editor = getDetachedViewerEditor()
  editor.commands.setContent(normalizeEditorContent(content))
  return editor.getHTML()
}

/**
 * 將 Markdown / 編輯器內容轉成 HTML。
 * 透過 Promise 佇列依序執行，並使用脫離畫面的獨立 Editor，
 * 避免跟 SectionEditor / DraftChange 的 useEditor 搶同一個 DOM。
 */
export const contentToHtml = (content) => {
  if (content == null || content === '') return Promise.resolve('')
  if (typeof content === 'string' && content.startsWith('<p>')) return Promise.resolve(content)

  const task = contentToHtmlQueue.then(() => convertContentToHtmlSync(content))
  contentToHtmlQueue = task.catch(() => {})
  return task
}
